'use client';

import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Container } from '../../components/Container';
import { PCGDetails } from './components/WalkerDetails';
import { useMediaQuery } from '../../hooks';
import { WalkerServices } from './components/WalkerServices';
import { WalkerAbout } from './components/WalkerAbout';
import { WalkerReviewsContext } from './components/WalkerReviews';
import { WalkerEndorsementsContext } from './components/WalkerEndorsements';
import { WalkerSchedule } from './components/WalkerSchedule';
import { ServicesGallery } from '.';
import { WalkerProfileProps } from '../../pages/walker-profile/[promoCode]';
import { pxToNumber } from '../../wag-react/common/utils/pxToNumber';
import { theme } from '../../wag-react/themes/base-theme';
import { Box } from '../../wag-react/next-components/box';
import dynamic from 'next/dynamic';
type Props = {
  data: WalkerProfileProps['value'];
  servicesGallery: ServicesGallery;
  queryParams: Record<string, string>;
  identifier: string;
};
export const pcgTabs = ['About', 'Services'] as const;
export const pcgTabsDesktop = ['Reviews & Endorsements'] as const;
export const pcgTabsMobile = ['Reviews', 'Endorsements'] as const;
export type PCGTab = typeof pcgTabs[number] | typeof pcgTabsDesktop[number] | typeof pcgTabsMobile[number];
const ImageGallery = dynamic(() => import('./components/ImageGallery').then(mod => mod.ImageGallery), {
  ssr: false
});
export const PCGProfileContainer = ({
  data: {
    reviews,
    servicesOffered,
    endorsements,
    achievements,
    availability,
    specialties,
    homePhotos,
    homeInfo,
    profile,
    gallery,
    serviceAddons,
    location
  },
  servicesGallery,
  identifier,
  queryParams
}: Props) => {
  const [selectedTab, setSelectedTab] = useState<PCGTab>(pcgTabs[0]);
  const {
    matched: isMobile
  } = useMediaQuery({
    maxWidth: pxToNumber(theme.breakpoints[0]) - 1
  });
  const tabElements = isMobile ? pcgTabsMobile : pcgTabsDesktop;
  return <>
      {/* Infer V6 Types until we remove the old version and retype page props */}
      <ImageGallery gallery={gallery} isMobile={isMobile} data-sentry-element="ImageGallery" data-sentry-source-file="PCGProfileContainer.tsx" />
      <Box className="shadow-lg" position="relative" data-sentry-element="Box" data-sentry-source-file="PCGProfileContainer.tsx">
        <Box mx="auto" width={1} px={[0, 0, '24px']} maxWidth={['100%', '100%', '1100px']} data-sentry-element="Box" data-sentry-source-file="PCGProfileContainer.tsx">
          <PCGDetails selectedTab={selectedTab} location={location} setSelectedTab={setSelectedTab} profile={profile} totalReviews={reviews?.total} pcgTabs={[...pcgTabs, ...tabElements]} data-sentry-element="PCGDetails" data-sentry-source-file="PCGProfileContainer.tsx" />
        </Box>
      </Box>
      <Box bg={theme.colors.whiteSmoke} overflow="hidden" data-sentry-element="Box" data-sentry-source-file="PCGProfileContainer.tsx">
        <Container px={[0, 0, '24px']} py={['30px', '50px']} width={['92%', '92%', '100%']} mx="auto" maxWidth={['100%', '100%', '1100px']} display="flex" flexDirection={['column', 'column', 'row']} data-sentry-element="Container" data-sentry-source-file="PCGProfileContainer.tsx">
          <Box width={['100%', '70%']} pr={2} data-sentry-element="Box" data-sentry-source-file="PCGProfileContainer.tsx">
            <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="PCGProfileContainer.tsx">
              <motion.div key={selectedTab} initial={{
              y: 10,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} exit={{
              y: -10,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} style={{
              maxWidth: '570px'
            }} data-sentry-element="unknown" data-sentry-source-file="PCGProfileContainer.tsx">
                {selectedTab === 'About' && <WalkerAbout profile={profile} specialties={specialties} homePhotos={homePhotos} homeInfo={homeInfo} achievements={achievements} />}

                {selectedTab === 'Services' && (servicesOffered?.items?.length ? <WalkerServices servicesOffered={servicesOffered} specialties={specialties} homePhotos={homePhotos} serviceAddons={serviceAddons} reviews={reviews} servicesGallery={servicesGallery} /> : null)}

                {selectedTab === 'Reviews & Endorsements' && <>
                    <WalkerReviewsContext averageRating={profile.averageRating} identifier={identifier} reviews={reviews} />
                    <WalkerEndorsementsContext identifier={identifier} endorsements={endorsements} containerProps={{
                  mt: 3
                }} />
                  </>}

                {selectedTab === 'Reviews' && <WalkerReviewsContext averageRating={profile.averageRating} identifier={identifier} reviews={reviews} />}

                {selectedTab === 'Endorsements' && <WalkerEndorsementsContext identifier={identifier} endorsements={endorsements} />}
              </motion.div>
            </AnimatePresence>
          </Box>
          <WalkerSchedule identifier={identifier} profile={profile} availability={availability} servicesOffered={servicesOffered} queryParams={queryParams} containerProps={{
          width: ['100%', '100%', '30%'],
          height: ['auto', 'fit-content'],
          textAlign: 'center',
          minWidth: ['310px', '340px'],
          maxWidth: '350px',
          mx: ['auto', 'auto', 'initial']
        }} data-sentry-element="WalkerSchedule" data-sentry-source-file="PCGProfileContainer.tsx" />
        </Container>
      </Box>
    </>;
};